import { motion } from 'framer-motion';
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  TicketIcon,
  ShareIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import SmallInfo from './SmallInfo';
import {
  formatDate,
  formatPhoneNumber,
  generateRecurringEventICS,
  getEventCalendarSummary,
  parseRecurrenceRule,
  getHumanReadableRecurrence,
  calculateEventDuration,
  formatDuration,
  getEffectiveDateRange,
} from '@/lib/util';
import Button from './Button';
import Modal from './Modal';
import { useState, useEffect } from 'react';

export default function QuickInfoPanel({ event, className = '' }) {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  const [todayDate, setTodayDate] = useState('');

  // Set today's date on client-side only to prevent hydration mismatch
  useEffect(() => {
    setTodayDate(new Date().toISOString().split('T')[0]);
  }, []);

  // Extract logistics information from event description
  const extractLogistics = (description) => {
    if (!description) return null;

    try {
      // Convert HTML to text for parsing (works on both server and client)
      let textContent = description;
      // Strip HTML tags using regex (SSR-safe)
      textContent = textContent.replace(/<[^>]*>/g, ' ');
      // Decode HTML entities
      textContent = textContent
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
      // Clean up whitespace
      textContent = textContent.replace(/\s+/g, ' ').trim();

      // Look for logistics patterns - including parking information
      const logisticsPatterns = [
        /free\s+visitor\s+parking\s+is\s+available\s+at\s+([^.\n]+)/i,
        /parking[:\s]+([^.\n]+)/i,
        /transportation[:\s]+([^.\n]+)/i,
        /arrive[:\s]+([^.\n]+)/i,
        /start\s+time[:\s]+([^.\n]+)/i,
        /logistics[:\s]+([^.\n]+)/i,
        /logistics\s+is\s+available\s+at\s+([^.\n]+)/i,
        /logistics\s+available\s+at\s+([^.\n]+)/i,
        /logistics\s+at\s+([^.\n]+)/i,
      ];

      for (const pattern of logisticsPatterns) {
        const match = textContent.match(pattern);
        if (match) return match[1] || match[0];
      }
      return null;
    } catch (error) {
      console.error('Error extracting logistics:', error);
      return null;
    }
  };

  // Handle calendar click for recurring events
  const handleCalendarClick = () => {
    // Always open the configuration modal for better user control
    openTimeSlotModal();
  };

  // Generate calendar event with selected time slots
  const generateCalendarEventWithSelectedSlots = (selectedSlots) => {
    // Create a modified event object with selected time slots and custom dates
    const eventToProcess = {
      ...event,
      times: selectedSlots.map((index) => event.times[index]),
    };

    // Use custom date range if specified
    if (useCustomDateRange && customStartDate && customEndDate) {
      eventToProcess.recurrencedatestart = customStartDate;
      eventToProcess.recurrencedateend = customEndDate;
    }

    const icsContent = generateRecurringEventICS(eventToProcess);
    if (icsContent) {
      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.title || 'event'}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
    setShowTimeSlotModal(false);
  };

  // Helper function to format date for date input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  };

  // Initialize selected time slots when modal opens
  const openTimeSlotModal = () => {
    if (event.times && Array.isArray(event.times) && event.times.length > 1) {
      // Default to selecting all time slots
      setSelectedTimeSlots(event.times.map((_, index) => index));
    } else if (
      event.times &&
      Array.isArray(event.times) &&
      event.times.length === 1
    ) {
      // Single time slot - select it by default
      setSelectedTimeSlots([0]);
    }

    // Calculate effective dates (filter out past dates)
    if (isrecurring && parsedRecurrenceRule) {
      const effectiveDates = getEffectiveDateRange(
        parsedRecurrenceRule,
        event.recurrencedatestart,
        event.recurrencedateend
      );

      // Use effective dates instead of original dates
      if (effectiveDates.effectiveStart) {
        setCustomStartDate(formatDateForInput(effectiveDates.effectiveStart));
      }
      if (effectiveDates.effectiveEnd) {
        setCustomEndDate(formatDateForInput(effectiveDates.effectiveEnd));
      }
    } else {
      // Initialize with original dates if no recurrence rule
      if (event.recurrencedatestart) {
        setCustomStartDate(formatDateForInput(event.recurrencedatestart));
      }
      if (event.recurrencedateend) {
        setCustomEndDate(formatDateForInput(event.recurrencedateend));
      }
    }

    setShowTimeSlotModal(true);
  };

  // Toggle time slot selection
  const toggleTimeSlot = (index) => {
    setSelectedTimeSlots((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // Select all time slots
  const selectAllTimeSlots = () => {
    setSelectedTimeSlots(event.times.map((_, index) => index));
  };

  // Deselect all time slots
  const deselectAllTimeSlots = () => {
    setSelectedTimeSlots([]);
  };

  // Share event function
  const shareEvent = () => {
    if (navigator.share) {
      navigator.share({
        title: event.title || 'Event',
        text: `Check out this event: ${event.title}`,
        url: window.location.href,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      // You could add a toast notification here
    }
    setShowShareMenu(false);
  };

  // Get calendar summary for this event
  const calendarSummary = getEventCalendarSummary(event);

  // Extract registration location from timeinfo field
  const extractRegistrationLocation = (timeinfo) => {
    if (!timeinfo) return null;

    try {
      // Look for the specific pattern in the registration info
      // This handles the case: "located at 450 Auburn Ave N.E. Atlanta Georgia 30312"
      const fullAddressMatch = timeinfo.match(/located\s+at\s+([^.]*?\d{5})/i);
      if (fullAddressMatch) {
        return fullAddressMatch[1].trim();
      }

      // Fallback: look for any address pattern with street name and zip code
      const fallbackMatch = timeinfo.match(
        /(\d+\s+[A-Za-z\s]+(?:Ave|Street|St|Road|Rd|Boulevard|Blvd|Drive|Dr|Lane|Ln|Place|Pl|Court|Ct|Way|Terrace|Ter|Circle|Cir|Avenue)\s+[A-Za-z\s]+(?:[A-Z]{1,2}\.)*\s+[A-Za-z\s]+\s+\d{5})/i
      );
      if (fallbackMatch) {
        return fallbackMatch[1].trim();
      }

      return null;
    } catch (error) {
      console.error('Error extracting registration location:', error);
      return null;
    }
  };

  // Safety check for event data
  if (!event) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className={`bg-white rounded p-6 ${className}`}
      >
        <h2 className="text-xl font-bold text-brown-200 mb-6">Event Details</h2>
        <p className="text-sm ">Event information not available.</p>
      </motion.div>
    );
  }

  const {
    dates,
    datestart,
    dateend,
    times,
    timestart,
    timeend,
    feeinfo,
    isfree,
    isregresrequired,
    regresurl,
    regresinfo,
    infoUrl,
    contacttelephonenumber,
    contactemailaddress,
    contactname,
    location,
    meetingpoint,
    isrecurring,
    recurrencedatestart,
    recurrencedateend,
    recurrencerule,
    description,
    timeinfo,
  } = event;

  const isRegistrationRequired = isregresrequired === 'true';
  const isFree = isfree === 'true';
  const hasFeeInfo = feeinfo && feeinfo.trim() !== '';

  // Parse recurrence rule for better display
  const parsedRecurrenceRule = recurrencerule
    ? parseRecurrenceRule(recurrencerule)
    : null;
  const humanReadableRecurrence = parsedRecurrenceRule
    ? getHumanReadableRecurrence(parsedRecurrenceRule)
    : 'Recurring Event';

  // Calculate event duration from times
  let eventDuration = null;
  if (times && Array.isArray(times) && times.length > 0) {
    const firstTime = times[0];
    if (firstTime.timestart && firstTime.timeend) {
      eventDuration = calculateEventDuration(
        firstTime.timestart,
        firstTime.timeend
      );
    }
  } else if (timestart && timeend) {
    eventDuration = calculateEventDuration(timestart, timeend);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.1 }}
      className={`bg-white rounded p-6 max-md:mt-6 ${className}`}
    >
      <h2 className="text-xl font-bold text-brown-200 mb-6">Event Details</h2>

      {/* Event Category and Type Tags */}
      {(event.category ||
        (event.types &&
          Array.isArray(event.types) &&
          event.types.length > 0)) && (
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {/* Event Category Badge */}
            {event.category && (
              <span className="inline-flex items-center px-3 pt-1.5 py-1 rounded-full bg-green-100 text-white text-xxs font-black tracking-wide">
                {event.category}
              </span>
            )}

            {/* Event Types Badges */}
            {event.types &&
              Array.isArray(event.types) &&
              event.types.length > 0 && (
                <>
                  {event.types.slice(0, 3).map((type) => (
                    <span
                      key={type}
                      className="inline-flex items-center px-2 pt-1.5 py-1 rounded-full bg-beige-200/90 text-brown-200 text-xxs font-bold backdrop-blur-sm"
                    >
                      {type}
                    </span>
                  ))}
                </>
              )}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Date & Time Section */}
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <CalendarIcon className="w-5 h-5 text-green-100  flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-black text-brown-200 mb-1">
                Date & Time
              </h3>
              {isrecurring ? (
                <div className="space-y-2">
                  <p className="text-xs ">
                    <span className="font-black">Schedule:</span>{' '}
                    {humanReadableRecurrence}
                  </p>

                  <div className="mt-3">
                    {recurrencedatestart && (
                      <p className="text-xs ">
                        From: {formatDate(recurrencedatestart)}
                      </p>
                    )}
                    {recurrencedateend && (
                      <p className="text-xs ">
                        To: {formatDate(recurrencedateend)}
                      </p>
                    )}
                  </div>

                  {event.times &&
                    Array.isArray(event.times) &&
                    event.times.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-black mb-1">Times:</p>
                        {event.times.map((time, index) => (
                          <p key={index} className="text-xs ">
                            {time.timestart} - {time.timeend}
                          </p>
                        ))}
                      </div>
                    )}
                  {timeinfo && <p className="text-xs mt-3">{timeinfo}</p>}
                  {eventDuration && (
                    <p className="text-xs ">
                      <span className="font-black">Duration:</span>{' '}
                      {formatDuration(eventDuration)}
                    </p>
                  )}
                  {!eventDuration &&
                    event.times &&
                    event.times[0] &&
                    event.times[0].timestart &&
                    event.times[0].timeend && (
                      <p className="text-xs ">
                        <span className="font-black">Duration:</span>{' '}
                        {formatDuration(
                          calculateEventDuration(
                            event.times[0].timestart,
                            event.times[0].timeend
                          )
                        )}
                      </p>
                    )}

                  {/* Add to Calendar Button for Recurring Events */}
                  {/* <div className="mt-3">
                                        <Button
                                            onClick={handleCalendarClick}
                                            variant="primary"
                                            size="lg"
                                            fullWidth
                                            icon={<CalendarIcon className="w-5 h-5 -mt-1" />}
                                            iconPosition="left"
                                        >
                                            Configure Add to Calendar
                                        </Button>
                                        {calendarSummary && (
                                            <div className="text-xs text-center mt-1">
                                                <p>{calendarSummary.summary}</p>
                                            </div>
                                        )}
                                    </div> */}
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Show datestart if available */}
                  {datestart && (
                    <p className="text-xs ">
                      {formatDate(datestart)}
                      {dateend &&
                        datestart !== dateend &&
                        ` - ${formatDate(dateend)}`}
                    </p>
                  )}
                  {/* Fallback to dates field if no datestart */}
                  {!datestart && dates && (
                    <p className="text-xs ">{dates}</p>
                  )}
                  {/* Fallback to recurrencedatestart if no datestart or dates */}
                  {!datestart && !dates && recurrencedatestart && (
                    <p className="text-xs ">
                      {formatDate(recurrencedatestart)}
                      {recurrencedateend &&
                        recurrencedatestart !== recurrencedateend &&
                        ` - ${formatDate(recurrencedateend)}`}
                    </p>
                  )}
                  {/* Show timestart if available */}
                  {timestart && (
                    <p className="text-xs ">
                      {timestart}
                      {timeend && timestart !== timeend && ` - ${timeend}`}
                    </p>
                  )}
                  {/* Fallback to times array if no timestart */}
                  {!timestart &&
                    times &&
                    Array.isArray(times) &&
                    times.length > 0 && (
                      <div className="mt-1">
                        {times.map((time, index) => (
                          <p key={index} className="text-xs ">
                            {time.timestart}
                            {time.timeend && ` - ${time.timeend}`}
                          </p>
                        ))}
                      </div>
                    )}
                  {/* Show message if no date/time info at all */}
                  {!datestart && !dates && !recurrencedatestart && !timestart && (!times || times.length === 0) && (
                    <p className="text-xs text-brown-100/50">
                      Contact the park for date and time information
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Location Section */}
        {(location || meetingpoint) && (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <MapPinIcon className="w-5 h-5 text-green-100  flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-brown-200 mb-1">
                  Location
                </h3>

                {/* General Location */}
                {location && (
                  <div className="mb-2">
                    <p className="text-xs ">{location}</p>
                  </div>
                )}

                {/* Meeting Point */}
                {meetingpoint && (
                  <div>
                    <p className="text-xs  font-black mb-1">Meeting Point</p>
                    <p className="text-xs ">{meetingpoint}</p>
                  </div>
                )}

                {/* Get Directions Button */}
                {(event.latitude ||
                  event.longitude ||
                  location ||
                  meetingpoint ||
                  event.parkFullName ||
                  event.parkfullname) && (
                  <div className="mt-3">
                    <Button
                      onClick={() => {
                        const destination = encodeURIComponent(
                          location ||
                            meetingpoint ||
                            event.parkFullName ||
                            event.parkfullname
                        );
                        const url = `https://www.google.com/maps/search/?api=1&query=${destination}`;
                        window.open(url, '_blank');
                      }}
                      variant="secondary"
                      size="lg"
                      fullWidth
                      icon={<MapPinIcon className="w-5 h-5 -mt-1" />}
                      iconPosition="left"
                    >
                      Get Directions
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cost Section */}
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <CurrencyDollarIcon className="w-5 h-5 text-green-100 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-brown-200 mb-1">
                Cost
              </h3>
              <div className="flex items-center gap-2">
                {isFree ? (
                  <CheckCircleIcon className="w-4 h-4 text-green-100" />
                ) : (
                  <ExclamationTriangleIcon className="w-4 h-4 text-coral-100" />
                )}
                <p className="text-xs ">{isFree ? 'Free' : 'Paid Event'}</p>
              </div>
              {hasFeeInfo && <p className="text-xs  mt-1">{feeinfo}</p>}
            </div>
          </div>
        </div>

        {/* Registration Section */}
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <TicketIcon className="w-5 h-5 text-green-100 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-brown-200 mb-1">
                Registration
              </h3>
              <div className="space-y-2">
                {isRegistrationRequired && (
                  <div className="flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-4 h-4 text-coral-100 -mt-1" />
                    {regresurl ? (
                      <a
                        href={regresurl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-black text-coral-100 hover:text-coral-100/80 transition-colors"
                      >
                        Registration Required
                      </a>
                    ) : (
                      <p className="text-xs font-black">
                        Registration Required
                      </p>
                    )}
                  </div>
                )}

                {!isRegistrationRequired && (
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-100 -mt-1" />
                    <p className="text-xs">No Registration Required</p>
                  </div>
                )}

                {regresurl && (
                  <a
                    href={regresurl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-xs text-green-100 hover:text-green-100/80 font-black transition-colors"
                  >
                    <TicketIcon className="w-4 h-4 -mt-1" />
                    Register Now
                  </a>
                )}

                {infoUrl && (
                  <a
                    href={infoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-xs text-green-100 hover:text-green-100/80 font-black transition-colors"
                  >
                    <InformationCircleIcon className="w-4 h-4 -mt-1" />
                    More Information
                  </a>
                )}

                {regresinfo && regresinfo.trim() !== '' && (
                  <div className="mt-3 pt-3 border-t border-beige-100">
                    <p className="text-xs font-black text-brown-200 mb-1">
                      Registration Information
                    </p>
                    <p className="text-xs leading-relaxed">{regresinfo}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        {(contactname || contacttelephonenumber || contactemailaddress) && (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <UserGroupIcon className="w-5 h-5 text-green-100 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-brown-200 mb-1">
                  Contact
                </h3>
                <div className="space-y-1">
                  {contactname && <p className="text-xs">{contactname}</p>}
                  {contacttelephonenumber && (
                    <a
                      href={`tel:${contacttelephonenumber}`}
                      className="block text-xs text-green-100 hover:text-green-100/80 transition-colors"
                    >
                      {formatPhoneNumber(contacttelephonenumber) ||
                        contacttelephonenumber}
                    </a>
                  )}
                  {contactemailaddress && (
                    <a
                      href={`mailto:${contactemailaddress}`}
                      className="block text-xs text-green-100 hover:text-green-100/80 transition-colors"
                    >
                      {contactemailaddress}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Share Event Button */}
      <div className="mt-6 pt-6 border-t border-beige-100">
        <div className="relative">
          <Button
            onClick={() => setShowShareMenu(!showShareMenu)}
            variant="purple"
            size="lg"
            fullWidth
            icon={<ShareIcon className="w-5 h-5 -mt-1" />}
            iconPosition="left"
          >
            Share Event
          </Button>

          {showShareMenu && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-beige-100 rounded-lg shadow-lg p-2 z-10"
            >
              <button
                onClick={shareEvent}
                className="w-full text-left px-3 py-2 text-sm text-brown-200 hover:bg-beige-50 rounded transition-colors"
              >
                {navigator.share ? 'Share via native app' : 'Copy link'}
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Action Buttons
            {(regresurl || infoUrl) || (infoUrl && !regresurl) && (
            <div className="mt-6 pt-6 space-y-3">
                {(regresurl || infoUrl) && (
                    <div className="flex flex-col gap-2">
                        {regresurl && (
                            <a
                                href={regresurl}
                                target="_blank"
                                rel="noreferrer"
                                className="w-full bg-green-100 text-white text-sm font-bold py-3 px-4 rounded text-center hover:bg-green-100/95 transition-colors duration-200"
                            >
                                Register for Event
                            </a>
                        )}
                        {infoUrl && !regresurl && (
                            <a
                                href={infoUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="w-full bg-beige-200 text-brown-200 text-sm font-bold py-3 px-4 rounded text-center hover:bg-beige-200/80 transition-colors duration-200"
                            >
                                Learn More
                            </a>
                        )}
                    </div>
                )}
            </div>
            )} */}

      {/* Calendar Configuration Modal */}
      {/* <Modal
                isOpen={showTimeSlotModal}
                setIsOpen={setShowTimeSlotModal}
                title="Configure Calendar Event"
                maxWidth="max-w-2xl"
                content={
                    <div className="space-y-4">
                        {isrecurring && parsedRecurrenceRule && (
                            <div className="p-3 bg-beige-50 rounded-lg border border-beige-100">
                                <h4 className="text-sm font-semibold text-brown-200 mb-2">Recurrence Pattern</h4>
                                <p className="text-sm">{humanReadableRecurrence}</p>
                                {recurrencedatestart && recurrencedateend && (
                                    <div className="mt-2 space-y-1">
                                        <p className="text-xs">
                                            Original period: {formatDate(recurrencedatestart)} to {formatDate(recurrencedateend)}
                                        </p>
                                        {(() => {
                                            const effectiveDates = getEffectiveDateRange(
                                                parsedRecurrenceRule, 
                                                recurrencedatestart, 
                                                recurrencedateend
                                            );
                                            if (effectiveDates.hasDateShift) {
                                                return (
                                                    <div className="p-2 bg-green-50 border border-green-200 rounded text-xs">
                                                        <p className="text-green-700">
                                                            Start date adjusted from {formatDate(recurrencedatestart)} to {formatDate(effectiveDates.effectiveStart)}
                                                        </p>
                                                        <p className="text-blue-700">
                                                            Only future occurrences will be added to your calendar
                                                        </p>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <p className="text-sm">
                            Configure how you want this event added to your calendar:
                        </p>
                        
                        {isrecurring && (
                            <div className="p-3 bg-beige-50 rounded-lg border border-beige-100">
                                <div className="flex items-center gap-2 mb-3">
                                    <input
                                        type="checkbox"
                                        id="useCustomDateRange"
                                        checked={useCustomDateRange}
                                        onChange={(e) => setUseCustomDateRange(e.target.checked)}
                                        className="w-4 h-4 text-green-100 border-brown-200 rounded focus:ring-brown-400"
                                    />
                                    <label htmlFor="useCustomDateRange" className="text-sm font-semibold text-brown-200">
                                        Use custom date range
                                    </label>
                                </div>
                                
                                {useCustomDateRange && (
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs font-bold text-brown-200 mb-1">
                                                Start Date
                                            </label>
                                            <input
                                                type="date"
                                                value={customStartDate}
                                                onChange={(e) => setCustomStartDate(e.target.value)}
                                                min={todayDate}
                                                className="w-full px-3 py-2 text-sm border border-brown-200 rounded-md focus:ring-2 focus:ring-brown-400 focus:border-transparent"
                                            />
                                            <p className="text-xs mt-1">
                                                Suggested: {(() => {
                                                    if (parsedRecurrenceRule) {
                                                        const effectiveDates = getEffectiveDateRange(
                                                            parsedRecurrenceRule, 
                                                            recurrencedatestart, 
                                                            recurrencedateend
                                                        );
                                                        return formatDate(effectiveDates.effectiveStart);
                                                    }
                                                    return 'Today';
                                                })()}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-brown-200 mb-1">
                                                End Date
                                            </label>
                                            <input
                                                type="date"
                                                value={customEndDate}
                                                onChange={(e) => setCustomEndDate(e.target.value)}
                                                min={customStartDate || todayDate}
                                                className="w-full px-3 py-2 text-sm border border-brown-200 rounded-md focus:ring-2 focus:ring-brown-400 focus:border-transparent"
                                            />
                                            <p className="text-xs mt-1">
                                                Suggested: {(() => {
                                                    if (parsedRecurrenceRule) {
                                                        const effectiveDates = getEffectiveDateRange(
                                                            parsedRecurrenceRule, 
                                                            recurrencedatestart, 
                                                            recurrencedateend
                                                        );
                                                        return effectiveDates.effectiveEnd ? formatDate(effectiveDates.effectiveEnd) : '1 year from start';
                                                        }
                                                    return '1 year from start';
                                                })()}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                
                    
                            </div>
                        )}

                        {event.times && Array.isArray(event.times) && event.times.length > 0 ? (
                            <>
                                <div>
                                    <h4 className="text-sm font-semibold text-brown-200 mb-3">Time Slots</h4>
                                    
                                    <div className="flex gap-2 mb-3">
                                        <button
                                            onClick={selectAllTimeSlots}
                                            className="text-xs px-3 py-1 pt-1.5 bg-green-100 text-white rounded hover:bg-green-100/80"
                                        >
                                            Select All
                                        </button>
                                        <button
                                            onClick={deselectAllTimeSlots}
                                            className="text-xs px-3 py-1 pt-1.5 bg-black/10 text-black rounded hover:bg-black/20"
                                        >
                                            Deselect All
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    {event.times.map((timeSlot, index) => {
                                        // Calculate duration for this time slot
                                        const duration = timeSlot.timestart && timeSlot.timeend 
                                            ? calculateEventDuration(timeSlot.timestart, timeSlot.timeend)
                                            : null;
                                        
                                        return (
                                            <label
                                                key={index}
                                                className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                                                    selectedTimeSlots.includes(index)
                                                        ? 'border-green-100 bg-green-100/10'
                                                        : 'border-brown-100 hover:border-brown-200'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTimeSlots.includes(index)}
                                                    onChange={() => toggleTimeSlot(index)}
                                                    className="sr-only"
                                                />
                                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-3 ${
                                                    selectedTimeSlots.includes(index)
                                                        ? 'border-green-100 bg-green-100'
                                                        : 'border-brown-100'
                                                }`}>
                                                    {selectedTimeSlots.includes(index) && (
                                                        <CheckIcon className="w-3 h-3 text-white" />
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm font-bold text-brown-200">
                                                        {timeSlot.timestart} - {timeSlot.timeend}
                                                    </p>
                                                                                                <div className="flex items-center gap-4 text-xs">
                                                {duration && (
                                                    <span>Duration: {formatDuration(duration)}</span>
                                                )}
                                                {isrecurring && (
                                                    <span>
                                                        {calendarSummary?.totalEvents 
                                                            ? Math.ceil(calendarSummary.totalEvents / event.times.length)
                                                            : 'Multiple'
                                                        } occurrences
                                                    </span>
                                                )}
                                            </div>
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-xs">
                                <p className="text-yellow-800 font-medium">⚠️ No Time Slots Available</p>
                                <p className="text-yellow-700">
                                    This event doesn&apos;t have specific time slots defined.
                                </p>
                            </div>
                        )}
                        
                        <div className="flex gap-3 pt-4">
                            <Button
                                onClick={() => setShowTimeSlotModal(false)}
                                variant="outline"
                                size="md"
                                fullWidth
                                className="text-brown-200 border-brown-200"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => generateCalendarEventWithSelectedSlots(selectedTimeSlots)}
                                variant="primary"
                                size="md"
                                fullWidth
                                disabled={selectedTimeSlots.length === 0}
                            >
                                {isrecurring 
                                    ? `Add ${selectedTimeSlots.length} Time Slot${selectedTimeSlots.length !== 1 ? "s" : ""} to Calendar`
                                    : `Add Event to Calendar`
                                }
                            </Button>
                        </div>
                    </div>
                }
                buttonText="Close"
            /> */}
    </motion.div>
  );
}
